import { useCallback, useEffect, useRef, useState, type TouchEvent } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/home';
import SearchBox from '@/components/base/SearchBox';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);
  const touchStart = useRef(0);

  const goTo = useCallback((next: number) => {
    setIndex((next + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [index]);

  const onTouchStart = (event: TouchEvent) => {
    touchStart.current = event.touches[0].clientX;
  };

  const onTouchEnd = (event: TouchEvent) => {
    const delta = event.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 50) {
      goTo(delta < 0 ? index + 1 : index - 1);
    }
  };

  return (
    <section
      className="relative h-[500px] w-full overflow-hidden md:h-[620px]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="焦点影视轮播"
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title}焦点剧照`}
            title={`${slide.title} 粉色水蜜桃影视`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full max-w-[1280px] flex-col justify-center px-4 md:px-6">
        <div className="max-w-2xl" key={heroSlides[index].id}>
          <p className="animate-fade-up mb-3 inline-flex items-center gap-2 rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium tracking-wide text-background-100 backdrop-blur">
            <i className="ri-star-smile-line text-accent-300" aria-hidden="true"></i>
            {heroSlides[index].tags}
          </p>
          <h1 className="animate-fade-up font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {heroSlides[index].title}
          </h1>
          <p className="animate-fade-up mt-3 text-sm font-medium text-accent-300 md:text-base">
            {heroSlides[index].typeLabel}
          </p>
          <p className="animate-fade-up mt-4 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
            {heroSlides[index].description}
          </p>
          <div className="animate-fade-up mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${heroSlides[index].mediaId}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 cursor-pointer dark:text-foreground-950"
            >
              <i className="ri-play-circle-line text-lg" aria-hidden="true"></i>
              查看详情
            </Link>
            <Link
              to="/category"
              className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/50 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
            >
              浏览全部影视
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="animate-fade-up mt-6 w-full max-w-xl">
            <SearchBox variant="hero" />
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-6 z-10 flex items-center justify-center gap-2">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
            }`}
          ></button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => goTo(index - 1)}
        className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/40 bg-foreground-950/30 text-background-50 transition-colors hover:bg-foreground-950/60 md:left-6 md:h-11 md:w-11 cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        onClick={() => goTo(index + 1)}
        className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/40 bg-foreground-950/30 text-background-50 transition-colors hover:bg-foreground-950/60 md:right-6 md:h-11 md:w-11 cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl" aria-hidden="true"></i>
      </button>
    </section>
  );
}